import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const colors = ["#3B82F6", "#8B5CF6", "#10B981", "#F97316", "#06B6D4", "#22C55E", "#6366F1", "#F59E0B", "#EC4899", "#EAB308"];

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const tz = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tz * 60000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInput(v) {
  if (!v) return "";
  return new Date(v).toISOString();
}

export default function EventDialog({ open, onClose, event, contacts, onSaved, onDeleted, defaultStart, defaultEnd }) {
  const isEdit = !!event?.id;
  const [form, setForm] = useState({
    title: "",
    start_at: "",
    end_at: "",
    color: colors[0],
    contact_id: null,
    notes: "",
    reminder_label: "",
    all_day: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (event) {
      setForm({
        title: event.title || "",
        start_at: toLocalInput(event.start_at),
        end_at: toLocalInput(event.end_at),
        color: event.color || colors[0],
        contact_id: event.contact_id || null,
        notes: event.notes || "",
        reminder_label: event.reminder_label || "",
        all_day: !!event.all_day,
      });
    } else {
      const start = defaultStart || new Date();
      const end = defaultEnd || new Date(start.getTime() + 60 * 60 * 1000);
      setForm({
        title: "",
        start_at: toLocalInput(start.toISOString()),
        end_at: toLocalInput(end.toISOString()),
        color: colors[0],
        contact_id: null,
        notes: "",
        reminder_label: "",
        all_day: false,
      });
    }
  }, [open, event, defaultStart, defaultEnd]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.start_at || !form.end_at) {
      toast.error("Start and end are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_at: fromLocalInput(form.start_at),
        end_at: fromLocalInput(form.end_at),
      };
      const r = isEdit ? await api.put(`/events/${event.id}`, payload) : await api.post("/events", payload);
      toast.success(isEdit ? "Event updated" : "Event created");
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${event.id}`);
    toast.success("Event deleted");
    onDeleted?.(event.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="!max-w-md bg-white rounded-2xl border-slate-100 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-xl text-slate-900">
            {isEdit ? "Edit event" : "New event"}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Title</label>
            <input className="mr-input mt-1.5" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="event-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Start</label>
              <input type="datetime-local" className="mr-input mt-1.5" value={form.start_at} onChange={(e) => set("start_at", e.target.value)} data-testid="event-start-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">End</label>
              <input type="datetime-local" className="mr-input mt-1.5" value={form.end_at} onChange={(e) => set("end_at", e.target.value)} data-testid="event-end-input" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Linked contact</label>
            <select className="mr-input mt-1.5" value={form.contact_id || ""} onChange={(e) => set("contact_id", e.target.value || null)}>
              <option value="">None</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.full_name}{c.company ? ` — ${c.company}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Color</label>
            <div className="flex gap-1.5 mt-1.5">
              {colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set("color", c)}
                  className={`w-7 h-7 rounded-full border-2 transition-transform ${form.color === c ? "scale-110 border-slate-900" : "border-white outline outline-1 outline-slate-100"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Reminder label</label>
            <input className="mr-input mt-1.5" value={form.reminder_label} onChange={(e) => set("reminder_label", e.target.value)} placeholder="e.g. Bring term sheet" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Notes</label>
            <textarea className="mr-input mt-1.5 min-h-[80px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          {isEdit ? (
            <button onClick={remove} className="px-3 py-2 rounded-lg text-sm text-rose-500 hover:bg-rose-50 inline-flex items-center gap-2 font-heading" data-testid="event-delete-button">
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 font-heading">Cancel</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium font-heading hover:bg-slate-800 disabled:opacity-60" data-testid="event-save-button">
              {busy ? "Saving…" : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
